import React, { useState, useEffect } from 'react';
import { Calendar, Users, CheckCircle, CreditCard, ChevronLeft, ChevronRight, Lock, Key, Compass, Shield, ArrowUp, ShieldCheck, Clock, Mail, MessageSquare, Phone, AlertTriangle } from 'lucide-react';
import { fallbackRooms, fallbackBookings, getCleanImage } from '../utils/fallbackData';
import { Room, Booking } from '../types';
import LiqPayInlineTerminal from './LiqPayInlineTerminal';
import RobustRoomImage from './RobustRoomImage';
import DirectMessagingModal from './DirectMessagingModal';
import { translations } from '../utils/translations';
import { safeLocalStorage } from '../utils/storageWrapper';
import { countries } from '../utils/countryData';

export { countries };

interface BookingFormProps {
  imageVersion: string;
  currentLanguage: string;
  theme?: 'light' | 'dark';
}

const PENDING_TITLES: Record<string, string> = {
  en: 'Pending Final Confirmation',
  uk: 'Очікує фінального підтвердження',
  de: 'Ausstehende Bestätigung',
  es: 'Pendiente de Confirmación',
  fr: 'En Attente de Confirmation'
};

const PENDING_DESCS: Record<string, string> = {
  en: 'Your reservation request is received! Bookings with "Pay on arrival" require manual verification before final confirmation. Choose one of the direct options below to finalize your booking.',
  uk: 'Ваш запит на бронювання отримано! Бронювання зі способом оплати «Оплата при заїзді» потребують ручної верифікації перед остаточним підтвердженням. Оберіть один із варіантів нижче, щоб завершити.',
  de: 'Ihre Reservierungsanfrage ist eingegangen! Buchungen mit „Zahlung bei Ankunft“ erfordern eine manuelle Verifizierung vor der endgültigen Bestätigung. Wählen Sie eine Option unten, um abzuschließen.',
  es: '¡Se ha recibido su solicitud de reserva! Las reservas con "Pago en el momento de la llegada" requieren verificación manual antes de la confirmación final. Seleccione una opción a continuación para finalizar.',
  fr: 'Votre demande de réservation a été reçue ! Les réservations avec « Paiement à l\'arrivée » nécessitent une vérification manuelle avant la confirmation finale. Choisissez une option ci-dessous pour finaliser.'
};

export const validatePhoneNumber = (
  dialCode: string,
  phoneNumber: string,
  lang: string = 'en'
): string | null => {
  const digitsOnly = phoneNumber.replace(/[^0-9]/g, '');
  if (!digitsOnly) {
    switch (lang) {
      case 'uk': return 'Номер телефону є обов’язковим для продовження бронювання.';
      case 'de': return 'Die Telefonnummer ist für die Reservierung erforderlich.';
      case 'es': return 'El número de teléfono es obligatorio para la reserva.';
      case 'fr': return 'Le numéro de téléphone est obligatoire pour la réservation.';
      default: return 'Contact phone number is mandatory to proceed with the reservation.';
    }
  }

  const matchedCountry = countries.find(c => c.dialCode === dialCode);
  if (!matchedCountry) return null;

  const { minDigits, maxDigits, name, formatPlaceholder } = matchedCountry;
  const countryName = name[lang as keyof typeof name] || name['en'];
  const len = digitsOnly.length;

  if (minDigits === maxDigits) {
    if (len !== minDigits) {
      switch (lang) {
        case 'uk':
          return `Невірний номер. Для країни ${countryName} номер повинен містити рівно ${minDigits} цифр після коду (наприклад, ${formatPlaceholder}). Ви ввели ${len} цифр.`;
        case 'de':
          return `Ungültige Nummer. Für ${countryName} muss die Telefonnummer genau ${minDigits} Ziffern nach der Vorwahl enthalten (z. B. ${formatPlaceholder}). Sie haben ${len} eingegeben.`;
        case 'es':
          return `Número inválido. Para ${countryName}, el número debe tener exactamente ${minDigits} dígitos después del prefijo (ej. ${formatPlaceholder}). Ha introducido ${len}.`;
        case 'fr':
          return `Numéro invalide. Pour ${countryName}, le numéro de téléphone doit contenir exactement ${minDigits} chiffres après l'indicatif (ex. ${formatPlaceholder}). Vous en avez saisi ${len}.`;
        default:
          return `Invalid number. For ${countryName}, the phone number must have exactly ${minDigits} digits after the dial code (e.g. ${formatPlaceholder}). You entered ${len} digits.`;
      }
    }
  } else {
    if (len < minDigits || len > maxDigits) {
      switch (lang) {
        case 'uk':
          return `Невірний номер. Для країни ${countryName} номер повинен містити від ${minDigits} до ${maxDigits} цифр після коду (наприклад, ${formatPlaceholder}). Ви ввели ${len} цифр.`;
        case 'de':
          return `Ungültige Nummer. Für ${countryName} muss die Telefonnummer zwischen ${minDigits} und ${maxDigits} Ziffern nach der Vorwahl enthalten (z. B. ${formatPlaceholder}). Sie haben ${len} eingegeben.`;
        case 'es':
          return `Número inválido. Para ${countryName}, el número debe tener entre ${minDigits} y ${maxDigits} dígitos después del prefijo (ej. ${formatPlaceholder}). Ha introducido ${len}.`;
        case 'fr':
          return `Numéro invalide. Pour ${countryName}, le numéro doit contenir entre ${minDigits} et ${maxDigits} chiffres après l'indicatif (ex. ${formatPlaceholder}). Vous en avez saisi ${len}.`;
        default:
          return `Invalid number. For ${countryName}, the phone number must have between ${minDigits} and ${maxDigits} digits after the dial code (e.g. ${formatPlaceholder}). You entered ${len} digits.`;
      }
    }
  }

  return null;
};

export const MONTHS: Record<string, string[]> = {
  en: ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'],
  uk: ['Січень', 'Лютий', 'Березень', 'Квітень', 'Травень', 'Червень', 'Липень', 'Серпень', 'Вересень', 'Жовтень', 'Листопад', 'Грудень'],
  es: ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'],
  de: ['Januar', 'Februar', 'März', 'April', 'Mai', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'],
  fr: ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre']
};

export const WEEKDAYS: Record<string, string[]> = {
  en: ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'],
  uk: ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Нд'],
  es: ['Lu', 'Ma', 'Mi', 'Ju', 'Vi', 'Sá', 'Do'],
  de: ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'],
  fr: ['Lu', 'Ma', 'Me', 'Je', 'Ve', 'Sa', 'Di']
};

const sanitizeText = (text: string): string => {
  if (!text) return '';
  return text
    .replace(/<script[^>]*>([\s\S]*?)<\/script>/gi, '')
    .replace(/<[^>]*>/g, '')
    .replace(/javascript:/gi, '')
    .trim();
};

const sanitizeRoomsList = (list: Room[]): Room[] => {
  if (!Array.isArray(list) || list.length === 0) return fallbackRooms;
  return list.map(r => {
    if (!r) return r;
    const f = fallbackRooms.find(item => item.id === r.id);
    if (!f) return r;
    
    const img = getCleanImage(r.id, r.image);
    let rawImgs = Array.isArray(r.images) && r.images.length > 0 ? r.images : [r.image];
    const imgs = rawImgs.map((item, idx) => getCleanImage(r.id, item, idx));
    
    return { ...r, image: img, images: imgs, amenities: f.amenities };
  });
};

export const getLocalizedError = (key: string, lang: string): string => {
  const dictionary: Record<string, Record<string, string>> = {
    overlap: {
      en: "Selected dates overlap with an existing booking. Please choose another date range.",
      uk: "Вибрані дати збігаються з існуючим бронюванням. Будь ласка, оберіть інший діапазон дат.",
      es: "Las fechas seleccionadas coinciden con una reserva existente. Por favor, elija otro rango de fechas.",
      fr: "Les dates sélectionnées chevauchent une réservation existante. Veuillez choisir une autre plage de dates."
    },
    overlap_simple: {
      en: "The selected dates overlap with an existing booking. Please pick another range.",
      uk: "Вибрані дати збігаються з існуючим бронюванням. Будь ласка, оберіть інший діапазон.",
      es: "Las fechas seleccionadas coinciden con una reserva existente. Por favor, elija otro rango.",
      fr: "Les dates sélectionnées chevauchent une réservation existante. Veuillez choisir une autre plage."
    },
    max_nights: {
      en: "The maximum reservation length is 30 consecutive nights. Please adjust your dates and try again.",
      uk: "Максимальна тривалість бронювання становить 30 ночей поспіль. Будь ласка, скоригуйте дати та спробуйте ще раз.",
      es: "La duración máxima de la reserva es de 30 noches consecutivas. Por favor, ajuste sus fechas e inténtelo de nuevo.",
      fr: "La durée maximale de la réservation est de 30 nuits consécutives. Veuillez ajuster vos dates et réessayer."
    },
    date_order: {
      en: "Check-Out date must be after Check-In date.",
      uk: "Дата виїзду має бути пізнішою за дату заїзду.",
      es: "La fecha de salida debe ser posterior a la fecha de entrada.",
      fr: "La date de départ doit être postérieure à la date d'arrivée."
    },
    name_required: {
      en: "Please enter the guest's full name.",
      uk: "Будь ласка, введіть повне ім'я гостя.",
      es: "Por favor, introduzca el nombre completo del huésped.",
      fr: "Veuillez saisir le nom complet du client."
    },
    email_required: {
      en: "Please enter the email address.",
      uk: "Будь ласка, введіть адресу електронної пошти.",
      es: "Por favor, introduzca la dirección de correo electrónico.",
      fr: "Veuillez saisir l'adresse e-mail."
    },
    dates_required: {
      en: "Please select both Check-In and Check-Out dates.",
      uk: "Будь ласка, виберіть дати заїзду та виїзду.",
      es: "Por favor, seleccione las fechas de entrada y salida.",
      fr: "Veuillez sélectionner les dates d'arrivée et de départ."
    },
    security_invalid: {
      en: "Security Validation Failure: Invalid name or email input.",
      uk: "Помилка перевірки безпеки: Неприпустиме ім'я або адреса електронної пошти.",
      es: "Fallo de validación de seguridad: Nombre o correo electrónico no válidos.",
      fr: "Échec de la validation de sécurité : Nom ou adresse e-mail invalide."
    }
  };
  return dictionary[key]?.[lang] || dictionary[key]?.['en'] || key;
};

export default function BookingForm({ imageVersion, currentLanguage, theme = 'light' }: BookingFormProps) {
  const t = (key: string) => {
    return translations[currentLanguage]?.[key] || translations['en']?.[key] || key;
  };

  const [rooms, setRooms] = useState<Room[]>(() => {
    if (typeof window !== 'undefined') {
      try {
        const saved = safeLocalStorage.getItem('cozzydine_rooms_v21');
        if (saved) {
          const parsed = JSON.parse(saved);
          return sanitizeRoomsList(parsed);
        }
      } catch (e) {
        console.warn('Could not read rooms from safeLocalStorage:', e);
      }
    }
    return sanitizeRoomsList(fallbackRooms);
  });

  const [bookings, setBookings] = useState<Booking[]>(() => {
    if (typeof window !== 'undefined') {
      try {
        const saved = safeLocalStorage.getItem('cozzydine_bookings_v21');
        if (saved) return JSON.parse(saved);
      } catch (e) {
        console.warn('Could not read bookings from safeLocalStorage:', e);
      }
    }
    return fallbackBookings;
  });

  const [filterType, setFilterType] = useState<'all' | 'private' | 'dorm'>('all');
  const [selectedRoomId, setSelectedRoomId] = useState<string>('double-room');
  
  const [imageIndexes, setImageIndexes] = useState<Record<string, number>>({});
  const [expandedRooms, setExpandedRooms] = useState<Record<string, boolean>>({});

  const [guestName, setGuestName] = useState('');
  const [guestEmail, setGuestEmail] = useState('');
  const [phoneCountry, setPhoneCountry] = useState('+380');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [checkIn, setCheckIn] = useState('');
  const [checkOut, setCheckOut] = useState('');
  const [guestCount, setGuestCount] = useState<number>(1);
  const [confirmedBooking, setConfirmedBooking] = useState<Booking | null>(null);
  const [pendingBooking, setPendingBooking] = useState<Booking | null>(null);
  const [paymentMethod, setPaymentMethod] = useState<'liqpay' | 'reception'>('liqpay');
  const [bookingError, setBookingError] = useState<string | null>(null);
  const [isDirectMessagingOpen, setIsDirectMessagingOpen] = useState(false);

  const todayObj = new Date();
  const todayStr = todayObj.toISOString().split('T')[0];

  const triggerNotification = async (booking: Booking) => {
    try {
      await fetch('/api/notify-booking', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ booking })
      });
      console.log('[BookingForm] Automated notification dispatched successfully to cozzydinekyiv@gmail.com and +380662070475');
    } catch (err) {
      console.warn('Booking notification API trigger error:', err);
    }
  };

  const isDateBooked = (dateStr: string) => {
    return bookings.some(booking => {
      if (booking.roomId !== selectedRoomId) return false;
      if (booking.status !== 'confirmed' && booking.status !== 'pending') return false;
      return dateStr >= booking.checkIn && dateStr < booking.checkOut;
    });
  };

  useEffect(() => {
    setCheckIn('');
    setCheckOut('');
    setBookingError(null);
  }, [selectedRoomId]);

  useEffect(() => {
    if (checkIn && checkOut) {
      if (checkOut <= checkIn) {
        setBookingError(getLocalizedError('date_order', currentLanguage));
      } else {
        const d1 = new Date(checkIn);
        const d2 = new Date(checkOut);
        const diffTime = Math.abs(d2.getTime() - d1.getTime());
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        if (diffDays > 30) {
          setBookingError(getLocalizedError('max_nights', currentLanguage));
        } else {
          setBookingError(prev => {
            if (prev === getLocalizedError('date_order', currentLanguage) || 
                prev === getLocalizedError('max_nights', currentLanguage)) {
              return null;
            }
            return prev;
          });
        }
      }
    } else {
      setBookingError(prev => {
        if (prev === getLocalizedError('date_order', currentLanguage) || 
            prev === getLocalizedError('max_nights', currentLanguage)) {
          return null;
        }
        return prev;
      });
    }
  }, [checkIn, checkOut, currentLanguage]);

  useEffect(() => {
    try {
      safeLocalStorage.setItem('cozzydine_bookings_v21', JSON.stringify(bookings));
    } catch (e) {
      console.warn('Could not write bookings to safeLocalStorage:', e);
    }
  }, [bookings]);

  useEffect(() => {
    if (bookingError) {
      const isPhoneError = 
        bookingError.includes('phone') || 
        bookingError.includes('number') || 
        bookingError.includes('digit') || 
        bookingError.includes('номер') || 
        bookingError.includes('цифр') || 
        bookingError.includes('Telefon') || 
        bookingError.includes('Ziffern') || 
        bookingError.includes('teléfono') || 
        bookingError.includes('dígitos') || 
        bookingError.includes('téléphone') || 
        bookingError.includes('chiffre');

      const isNameError = 
        bookingError.includes('name') || 
        bookingError.includes('ім\'я') || 
        bookingError.includes('Name');

      const isEmailError = 
        bookingError.includes('email') || 
        bookingError.includes('адресу') || 
        bookingError.includes('Email') || 
        bookingError.includes('e-mail');

      if (isPhoneError) {
        const phoneError = validatePhoneNumber(phoneCountry, phoneNumber, currentLanguage);
        setBookingError(phoneError);
      } else if (isNameError && guestName.trim()) {
        setBookingError(null);
      } else if (isEmailError && guestEmail.trim()) {
        setBookingError(null);
      }
    }
  }, [phoneNumber, phoneCountry, guestName, guestEmail, currentLanguage, bookingError]);

  useEffect(() => {
    try {
      const saved = safeLocalStorage.getItem('cozzydine_rooms_v21');
      if (saved) {
        const parsed = JSON.parse(saved);
        setRooms(sanitizeRoomsList(parsed));
      } else {
        setRooms(sanitizeRoomsList(fallbackRooms));
      }
    } catch (e) {
      console.warn('Could not read rooms inside effect:', e);
    }
  }, [imageVersion]);

  const handleNextImage = (roomId: string, imagesLength: number) => {
    setImageIndexes(prev => ({
      ...prev,
      [roomId]: ((prev[roomId] || 0) + 1) % imagesLength
    }));
  };

  const handlePrevImage = (roomId: string, imagesLength: number) => {
    setImageIndexes(prev => ({
      ...prev,
      [roomId]: ((prev[roomId] || 0) - 1 + imagesLength) % imagesLength
    }));
  };

  const getActiveRoom = () => rooms.find(r => r.id === selectedRoomId) || rooms[0];

  const getNightsCount = () => {
    if (!checkIn || !checkOut) return 0;
    const d1 = new Date(checkIn);
    const d2 = new Date(checkOut);
    const diffTime = Math.abs(d2.getTime() - d1.getTime());
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  };

  const calculateTotalPrice = () => {
    const room = getActiveRoom();
    if (!checkIn || !checkOut || !room) return 0;
    const nights = getNightsCount();
    let price = (nights || 1) * room.pricePerNight;

    if (nights >= 7) {
      price = Math.round(price * 0.85);
    } else if (nights >= 3) {
      price = Math.round(price * 0.90);
    }
    return price;
  };

  const handleBookingSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const room = getActiveRoom();
    if (!room) return;

    if (!guestName.trim()) {
      setBookingError(getLocalizedError('name_required', currentLanguage));
      return;
    }
    if (!guestEmail.trim()) {
      setBookingError(getLocalizedError('email_required', currentLanguage));
      return;
    }

    const phoneError = validatePhoneNumber(phoneCountry, phoneNumber, currentLanguage);
    if (phoneError) {
      setBookingError(phoneError);
      return;
    }

    if (!checkIn || !checkOut) {
      setBookingError(getLocalizedError('dates_required', currentLanguage));
      return;
    }

    if (checkOut <= checkIn) {
      setBookingError(getLocalizedError('date_order', currentLanguage));
      return;
    }

    const d1 = new Date(checkIn);
    const d2 = new Date(checkOut);
    const diffTime = Math.abs(d2.getTime() - d1.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    if (diffDays > 30) {
      setBookingError(getLocalizedError('max_nights', currentLanguage));
      return;
    }

    const hasOverlap = bookings.some(b => {
      if (b.roomId !== room.id) return false;
      if (b.status !== 'confirmed' && b.status !== 'pending') return false;
      return (checkIn < b.checkOut) && (checkOut > b.checkIn);
    });

    if (hasOverlap) {
      setBookingError(getLocalizedError('overlap_simple', currentLanguage));
      return;
    }

    setBookingError(null);
    const cleanName = sanitizeText(guestName);
    const cleanEmail = sanitizeText(guestEmail);
    const cleanPhone = phoneNumber ? sanitizeText(phoneNumber) : '';

    if (!cleanName || !cleanEmail) {
      setBookingError(getLocalizedError('security_invalid', currentLanguage));
      return;
    }

    const calculatedTotal = calculateTotalPrice();
    const fullPhone = cleanPhone ? `${phoneCountry} ${cleanPhone}` : '';

    if (paymentMethod === 'reception') {
      const newBooking: Booking = {
        id: `B-${Math.floor(1000 + Math.random() * 9000)}`,
        roomId: room.id,
        roomName: room.name,
        guestName: cleanName,
        guestEmail: cleanEmail,
        guestPhone: fullPhone,
        checkIn,
        checkOut,
        guests: guestCount,
        totalPrice: calculatedTotal,
        status: 'confirmed',
        paymentId: 'PAY-AT-RECEPTION',
        createdAt: new Date().toISOString()
      };
      setBookings([newBooking, ...bookings]);
      setConfirmedBooking(newBooking);
      triggerNotification(newBooking);
      setPendingBooking(null);

      setGuestName('');
      setGuestEmail('');
      setPhoneNumber('');
      setCheckIn('');
      setCheckOut('');
      setGuestCount(1);
    } else {
      const newBooking: Booking = {
        id: `B-${Math.floor(1000 + Math.random() * 9000)}`,
        roomId: room.id,
        roomName: room.name,
        guestName: cleanName,
        guestEmail: cleanEmail,
        guestPhone: fullPhone,
        checkIn,
        checkOut,
        guests: guestCount,
        totalPrice: calculatedTotal,
        status: 'pending',
        paymentId: `PAY-LIQPAY-${Math.random().toString(36).substring(2, 10).toUpperCase()}`,
        createdAt: new Date().toISOString()
      };

      setPendingBooking(newBooking);
      setConfirmedBooking(null);
    }
  };

  const handleInlinePaymentSuccess = (payId: string) => {
    if (!pendingBooking) return;
    const finalBooking: Booking = {
      ...pendingBooking,
      status: 'confirmed',
      paymentId: payId
    };

    setBookings([finalBooking, ...bookings]);
    setConfirmedBooking(finalBooking);
    triggerNotification(finalBooking);
    setPendingBooking(null);

    setGuestName('');
    setGuestEmail('');
    setPhoneNumber('');
    setCheckIn('');
    setCheckOut('');
    setGuestCount(1);
  };

  const filteredRooms = rooms.filter(r => filterType === 'all' || r.type === filterType);

  return (
    <div id="booking-form-root" className="flex flex-col gap-12 w-full">
      
      {/* LEFT COLUMN: Rooms list bento layout */}
      <div className="w-full space-y-6">
        
        {/* Category filters */}
        <div className={`explore-header filter-container-wrapper flex flex-col sm:flex-row justify-between items-center p-5 rounded-3xl gap-4 transition-all duration-300 ${
          theme === 'light'
            ? 'bg-white border border-zinc-200/80 shadow-2xs'
            : 'bg-gradient-to-b from-[#262626] to-[#1C1C1C] border-t border-[rgba(255,255,255,0.08)] shadow-2xl'
        }`}>
          <div className="flex flex-wrap items-center gap-3 shrink-0">
            <div className="flex items-center gap-2.5">
              <Compass className={`w-5 h-5 shrink-0 ${theme === 'light' ? 'text-emerald-850' : 'text-amber-400'}`} />
              <h2 className={`explore-title text-xs font-mono font-bold uppercase tracking-[0.1em] whitespace-nowrap ${theme === 'light' ? 'text-zinc-900' : 'text-[#FAFAFA]'}`}>
                {t('exploreRooms')}
              </h2>
            </div>
          </div>

          <div className={`flex p-1.5 rounded-2xl w-full sm:w-auto shrink-0 overflow-x-auto transition-colors ${
            theme === 'light'
              ? 'bg-zinc-50 border border-zinc-200'
              : 'bg-[#141210] border border-zinc-800'
          }`}>
            {(([ 'all', 'private', 'dorm' ] as const)).map((filterKey) => (
              <button
                key={filterKey}
                onClick={() => setFilterType(filterKey)}
                className={`flex-1 sm:flex-initial px-5 py-2 rounded-xl text-xs font-mono font-bold uppercase transition-all whitespace-nowrap cursor-pointer ${
                  filterType === filterKey
                    ? (theme === 'light' ? 'bg-zinc-950 text-white shadow-md' : 'bg-gradient-to-b from-[#2E2E2E] to-[#222222] text-amber-300 border border-[rgba(255,255,255,0.15)] shadow-md')
                    : (theme === 'light' ? 'text-zinc-500 hover:text-zinc-950' : 'text-zinc-400 hover:text-[#FAFAFA]')
                }`}
              >
                {filterKey === 'all' 
                  ? t('filterAll')
                  : filterKey === 'private'
                    ? t('filterPrivate')
                    : t('filterDorms')}
              </button>
            ))}
          </div>
        </div>

        {/* Room list items mapping */}
        <div className="space-y-4">
          {filteredRooms.map((room) => {
            const images = room.images || [room.image];
            const currentImgIndexRaw = imageIndexes[room.id] || 0;
            const currentImgIndex = currentImgIndexRaw >= images.length ? 0 : currentImgIndexRaw;
            const rawImg = images[currentImgIndex] || room.image || '';
            const currentImg = getCleanImage(room.id, rawImg, currentImgIndex);
            const isSelected = selectedRoomId === room.id;
             return (
              <div 
                key={room.id}
                onClick={() => setSelectedRoomId(room.id)}
                className={`group border rounded-2xl overflow-hidden transition-all duration-300 grid grid-cols-1 md:grid-cols-12 gap-0 cursor-pointer ${
                  theme === 'light'
                    ? `bg-white hover:shadow-md ${isSelected ? 'border-zinc-950 ring-2 ring-zinc-950/10 shadow-sm' : 'border-zinc-200'}`
                    : `bg-gradient-to-b from-[#2E2E2E] to-[#222222] border-t border-[rgba(255,255,255,0.05)] shadow-md hover:shadow-lg ${isSelected ? 'border-amber-400 ring-2 ring-amber-400/20' : 'border-transparent'}`
                }`}
                style={theme === 'dark' ? { boxShadow: '0 8px 16px -4px rgba(0,0,0,0.4)' } : undefined}
              >
                {/* Visual Image area occupying 5 columns (increased width) */}
                <div className="md:col-span-5 h-[180px] md:h-auto relative overflow-hidden bg-zinc-900 min-h-[160px]">
                  <div className="md:absolute md:inset-0 w-full h-full">
                    <RobustRoomImage
                      roomId={room.id}
                      index={currentImgIndex}
                      fallbackUnsplashUrl={currentImg}
                      alt={room.name}
                      loading="lazy"
                      className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500 animate-fade-in"
                    />
                    
                    {/* Image sliders overlay */}
                    {images.length > 1 && (
                      <div className="absolute inset-x-2 bottom-2 flex items-center justify-between z-10">
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handlePrevImage(room.id, images.length);
                          }}
                          className="p-1.5 bg-zinc-900/80 hover:bg-zinc-950 text-white rounded-lg backdrop-blur-md shadow-lg border border-white/10 cursor-pointer"
                        >
                          <ChevronLeft className="w-3.5 h-3.5" />
                        </button>
                        
                        {/* Sliders indicator dots */}
                        <div className="flex gap-1">
                          {images.map((_, i) => (
                            <span 
                              key={i} 
                              className={`w-1 h-1 rounded-full transition-all ${
                                i === currentImgIndex ? 'bg-amber-400 w-2.5' : 'bg-white/55'
                              }`}
                            />
                          ))}
                        </div>

                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleNextImage(room.id, images.length);
                          }}
                          className="p-1.5 bg-zinc-900/80 hover:bg-zinc-950 text-white rounded-lg backdrop-blur-md shadow-lg border border-white/10 cursor-pointer"
                        >
                          <ChevronRight className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    )}

                    <div className="absolute top-3 left-3 bg-zinc-950 shadow-md text-white px-2.5 py-1 rounded-lg backdrop-blur-sm text-[10.5px] font-mono uppercase font-bold tracking-wider border border-white/10">
                      💵 {room.pricePerNight} ₴ {t('perNight') || '/ night'}
                    </div>
                  </div>
                </div>

                {/* Content occupying 7 columns */}
                <div className="md:col-span-7 p-4 sm:p-5 flex flex-col justify-between space-y-3.5">
                  <div className="space-y-2">
                    <div className="flex items-center">
                      <span className={`text-[10px] font-mono tracking-wider px-2 py-0.5 rounded-lg font-bold uppercase border ${
                        theme === 'light'
                          ? 'text-emerald-850 bg-emerald-50 border-emerald-100/60'
                          : 'text-emerald-400 bg-emerald-950/40 border-emerald-800/40'
                      }`}>
                        {t('room_type_' + room.type) || (room.type + ' Suite')}
                      </span>
                    </div>

                    <h3 className={`font-display font-extrabold text-2xl sm:text-2xl tracking-tight ${theme === 'light' ? 'text-blue-700' : 'text-blue-400'}`}>
                      {t('room_' + room.id + '_name') || room.name}
                    </h3>
                    <p className={`text-sm sm:text-sm leading-relaxed font-semibold line-clamp-2 md:line-clamp-3 ${theme === 'light' ? 'text-zinc-500' : 'text-[#A8A29E]'}`}>
                      {t('room_' + room.id + '_desc') || room.description}
                    </p>
                  </div>

                  {/* Amenities (compact display) */}
                  <div className="flex flex-wrap gap-1 md:gap-1.5 animate-fade-in">
                    {(expandedRooms[room.id] ? room.amenities : room.amenities.slice(0, 2)).map((am, idx) => (
                      <span 
                        key={idx} 
                        className={`text-[10px] font-mono px-2 py-0.5 rounded-lg font-bold border ${
                          theme === 'light'
                            ? 'text-zinc-500 bg-zinc-50 border-zinc-200/50'
                            : 'text-[#A8A29E] bg-[#141210]/40 border-zinc-800/40'
                        }`}
                      >
                        ✓ {t(am) || am}
                      </span>
                    ))}
                    {room.amenities.length > 2 && (
                      <button 
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setExpandedRooms(prev => ({ ...prev, [room.id]: !prev[room.id] }));
                        }}
                        className={`text-[10px] font-mono transition-colors px-2 py-0.5 border rounded-lg font-bold cursor-pointer ${
                          theme === 'light'
                            ? 'text-emerald-850 hover:text-emerald-950 bg-emerald-50 hover:bg-emerald-100 border-emerald-100/40'
                            : 'text-amber-300 hover:text-amber-400 bg-[#141210] hover:bg-[#222222] border-zinc-800'
                        }`}
                      >
                        {expandedRooms[room.id] ? t('lblShowLess') || 'show less' : `+${room.amenities.length - 2} ${t('lblMore') || 'more'}`}
                      </button>
                    )}
                  </div>

                  {/* Pricing and Action links */}
                  <div className={`pt-3 border-t flex items-center justify-between gap-3 ${theme === 'light' ? 'border-zinc-100' : 'border-zinc-800'}`}>
                    <p className="text-[10.5px] font-mono text-zinc-400">
                      {t('lblCapacity')}: <span className={`font-bold ${theme === 'light' ? 'text-zinc-900' : 'text-white'}`}>{room.capacity} {room.capacity === 1 ? t('lblGuestSingular') : t('lblGuestPlural')}</span>
                    </p>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedRoomId(room.id);
                        const formSec = document.getElementById('reservation-panel');
                        if (formSec) formSec.scrollIntoView({ behavior: 'smooth' });
                      }}
                      className="book-now-global !py-3 !px-6 !text-sm !rounded-xl shrink-0"
                    >
                      {t('btnBook') || 'Book Now'}
                    </button>
                  </div>
                </div>

              </div>
            );
          })}
        </div>

      </div>

      {/* RIGHT COLUMN: Reservation form block */}
      <div id="reservation-panel" className="w-full space-y-6">
        <div className={`transition-all duration-300 rounded-[1.8rem] p-6 sm:p-10 text-left ${
          theme === 'light'
            ? 'bg-white border border-zinc-200 shadow-sm'
            : 'bg-gradient-to-b from-[#262626] to-[#1C1C1C] border-t border-[rgba(255,255,255,0.08)] shadow-2xl'
        }`} style={theme === 'dark' ? { boxShadow: '0 12px 24px -6px rgba(0,0,0,0.5)' } : undefined}>
          <h2 className={`font-display font-extrabold text-lg sm:text-xl tracking-tight mb-3 flex items-center gap-2 ${
            theme === 'light' ? 'text-[#111827]' : 'text-white'
          }`}>
            <Calendar className={`w-5 h-5 ${theme === 'light' ? 'text-emerald-850' : 'text-amber-400'}`} />
            <span>{t('lblSecureHotelReservation') || 'Secure Hotel Reservation'}</span>
          </h2>

          <div className="mb-5 py-2 px-1 transition-all duration-300 hover:scale-[1.01] flex items-center justify-center gap-2.5 text-center">
            <span className="text-base inline-block animate-bounce select-none">🏷️✨</span>
            <p className="text-xs sm:text-sm font-black tracking-wide animate-pulse vl-prominent-blue">
              {t('lblLongStayOfferDesc') || 'Book 3+ nights for a 10% discount, or 7+ nights for 15% off.'}
            </p>
          </div>

          <form onSubmit={handleBookingSubmit} className="space-y-6 text-xs">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <label htmlFor="booking-guest-name" className={`text-[9px] font-mono font-black uppercase ${
                  theme === 'light' ? 'text-zinc-500' : 'text-[#A8A29E]'
                }`}>{t('lblGuestFullName') || 'Guest Name'}</label>
                <input
                  id="booking-guest-name"
                  type="text"
                  required
                  value={guestName}
                  onChange={(e) => setGuestName(e.target.value)}
                  placeholder="e.g. Liam Sterling"
                  aria-label="Guest Name"
                  className={`w-full mt-1 border rounded-xl px-4 py-3 text-xs focus:outline-none font-medium transition-all ${
                    theme === 'light'
                      ? 'border-zinc-200 bg-zinc-50 text-zinc-950 focus:ring-1 focus:ring-emerald-700'
                      : 'border-zinc-800 bg-[#141210] text-[#F7F5F0] focus:ring-1 focus:ring-amber-400/50'
                  }`}
                />
              </div>

              <div>
                <label htmlFor="booking-guest-email" className={`text-[9px] font-mono font-black uppercase ${
                  theme === 'light' ? 'text-zinc-500' : 'text-[#A8A29E]'
                }`}>{t('lblGuestEmailAddress') || 'Contact Email'}</label>
                <input
                  id="booking-guest-email"
                  type="email"
                  required
                  value={guestEmail}
                  onChange={(e) => setGuestEmail(e.target.value)}
                  placeholder="liam@concept.dev"
                  aria-label="Contact Email"
                  className={`w-full mt-1 border rounded-xl px-4 py-3 text-xs focus:outline-none font-medium transition-all ${
                    theme === 'light'
                      ? 'border-zinc-200 bg-zinc-50 text-zinc-950 focus:ring-1 focus:ring-emerald-700'
                      : 'border-zinc-800 bg-[#141210] text-[#F7F5F0] focus:ring-1 focus:ring-amber-400/50'
                  }`}
                />
              </div>

              <div>
                <label htmlFor="booking-contact-phone" className={`text-[9px] font-mono font-black uppercase ${
                  theme === 'light' ? 'text-zinc-500' : 'text-[#A8A29E]'
                }`}>{t('lblContactPhone') || 'Contact Phone Number'}</label>
                {/* Phone input wrapper in inline Booking Form */}
                <div className="flex gap-2 min-w-0 w-full mt-1">
                  <select
                    id="booking-phone-country"
                    value={phoneCountry}
                    onChange={(e) => setPhoneCountry(e.target.value)}
                    aria-label="Country Code"
                    className={`w-[90px] xs:w-[105px] sm:w-[130px] h-[42px] border rounded-xl px-1.5 sm:px-2 text-xs focus:outline-none font-bold shrink-0 cursor-pointer transition-all ${
                      theme === 'dark' 
                        ? 'bg-zinc-800/80 border-zinc-700 text-amber-400 focus:border-amber-500' 
                        : 'bg-white border-zinc-300 text-zinc-900 focus:border-amber-600'
                    }`}
                  >
                    {countries.map((c) => (
                      <option key={`${c.code}-${c.dialCode}`} value={c.dialCode} className={theme === 'dark' ? 'bg-zinc-900 text-amber-400' : 'bg-white text-zinc-900'}>
                        {c.flag} {c.code} {c.dialCode}
                      </option>
                    ))}
                  </select>
                  <div className="flex-1 min-w-0">
                    <input 
                      id="booking-contact-phone"
                      type="tel" 
                      required
                      value={phoneNumber}
                      onChange={(e) => setPhoneNumber(e.target.value.replace(/[^0-9]/g, ''))}
                      placeholder={t('lblPhonePlaceholder') || 'e.g. 50 123 4567'}
                      aria-label="Contact Phone Number"
                      className={`w-full h-[42px] border rounded-xl px-3 text-sm font-semibold focus:outline-none transition-all placeholder:text-zinc-500 font-mono ${
                        theme === 'dark' 
                          ? 'bg-zinc-800/80 border-zinc-700 text-white focus:border-amber-500' 
                          : 'bg-white border-zinc-300 text-zinc-900 focus:border-amber-600'
                      }`}
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <label htmlFor="check-in-date" className={`text-[9px] font-mono font-black uppercase ${
                  theme === 'light' ? 'text-zinc-500' : 'text-[#A8A29E]'
                }`}>{t('checkIn') || 'Check In'}</label>
                <input
                  id="check-in-date"
                  type="date"
                  required
                  value={checkIn}
                  min={todayStr}
                  onChange={(e) => setCheckIn(e.target.value)}
                  aria-label="Check-In Date"
                  className={`w-full mt-1 border rounded-xl px-3 py-3 text-xs focus:outline-none font-semibold transition-all ${
                    theme === 'light'
                      ? 'border-zinc-200 bg-zinc-50 text-zinc-950 focus:ring-1 focus:ring-emerald-700'
                      : 'border-zinc-800 bg-[#141210] text-[#F7F5F0] focus:ring-1 focus:ring-amber-400/50'
                  }`}
                />
              </div>
              <div>
                <label htmlFor="check-out-date" className={`text-[9px] font-mono font-black uppercase ${
                  theme === 'light' ? 'text-zinc-500' : 'text-[#A8A29E]'
                }`}>{t('checkOut') || 'Check Out'}</label>
                <input
                  id="check-out-date"
                  type="date"
                  required
                  value={checkOut}
                  min={checkIn || todayStr}
                  onChange={(e) => setCheckOut(e.target.value)}
                  aria-label="Check-Out Date"
                  className={`w-full mt-1 border rounded-xl px-3 py-3 text-xs focus:outline-none font-semibold transition-all ${
                    theme === 'light'
                      ? 'border-zinc-200 bg-zinc-50 text-zinc-950 focus:ring-1 focus:ring-emerald-700'
                      : 'border-zinc-800 bg-[#141210] text-[#F7F5F0] focus:ring-1 focus:ring-amber-400/50'
                  }`}
                />
              </div>
              <div>
                <label htmlFor="guest-count-select" className={`text-[9px] font-mono font-black uppercase ${
                  theme === 'light' ? 'text-zinc-500' : 'text-[#A8A29E]'
                }`}>{t('guests') || 'Guests'}</label>
                <select
                  id="guest-count-select"
                  value={guestCount}
                  onChange={(e) => setGuestCount(Number(e.target.value))}
                  aria-label="Number of Guests"
                  className={`w-full mt-1 border rounded-xl px-4 py-3 text-xs focus:outline-none font-bold shrink-0 cursor-pointer transition-all ${
                    theme === 'light'
                      ? 'border-zinc-200 bg-zinc-50 text-zinc-950 focus:ring-1 focus:ring-emerald-700'
                      : 'border-zinc-800 bg-[#141210] text-[#F7F5F0] focus:ring-1 focus:ring-amber-400/50'
                  }`}
                >
                  {Array.from({ length: 10 }, (_, idx) => (
                    <option key={idx + 1} value={idx + 1} className={theme === 'dark' ? 'bg-[#1C1C1C] text-[#FAFAFA]' : ''}>
                      {idx + 1} {idx === 0 ? t('lblGuestSingular') || 'Guest' : t('lblGuestPlural') || 'Guests'}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {bookingError && (
              <div className={`p-3 border rounded-xl text-[10.5px] font-mono flex items-center gap-2 transition-all animate-fade-in ${
                theme === 'light'
                  ? 'bg-red-50 border-red-200 text-red-900'
                  : 'bg-red-950/20 border-red-900/30 text-red-400'
              }`}>
                <span>⚠️ {bookingError}</span>
              </div>
            )}

            <div className={`pt-4 border-t ${theme === 'light' ? 'border-zinc-100' : 'border-zinc-850'}`}>
              <label className={`text-[9px] font-mono font-black uppercase block mb-2.5 ${
                theme === 'light' ? 'text-zinc-500' : 'text-[#A8A29E]'
              }`}>
                {t('lblPaymentMethod')}
              </label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <label className={`flex items-start gap-2.5 p-4 rounded-xl border-2 transition-all cursor-pointer ${
                  paymentMethod === 'liqpay'
                    ? (theme === 'light' ? 'border-emerald-800 bg-emerald-50/20' : 'border-amber-400 bg-amber-950/20')
                    : (theme === 'light' ? 'border-zinc-200 hover:border-zinc-300' : 'border-zinc-800 hover:border-zinc-700')
                }`}>
                  <input
                    type="radio"
                    name="paymentMethod"
                    value="liqpay"
                    checked={paymentMethod === 'liqpay'}
                    onChange={() => setPaymentMethod('liqpay')}
                    className="mt-0.5 text-emerald-800 focus:ring-emerald-800"
                  />
                  <div>
                    <span className={`block text-xs font-black uppercase ${theme === 'light' ? 'text-zinc-900' : 'text-white'}`}>
                      Pay Now
                    </span>
                    <span className="block text-[10.5px] text-zinc-400 mt-1 leading-relaxed">
                      Secured instant credit card transaction with fully automated booking confirmation.
                    </span>
                  </div>
                </label>

                <label className={`flex items-start gap-2.5 p-4 rounded-xl border-2 transition-all cursor-pointer ${
                  paymentMethod === 'reception'
                    ? (theme === 'light' ? 'border-emerald-800 bg-emerald-50/20' : 'border-amber-400 bg-amber-950/20')
                    : (theme === 'light' ? 'border-zinc-200 hover:border-zinc-300' : 'border-zinc-800 hover:border-zinc-700')
                }`}>
                  <input
                    type="radio"
                    name="paymentMethod"
                    value="reception"
                    checked={paymentMethod === 'reception'}
                    onChange={() => setPaymentMethod('reception')}
                    className="mt-0.5 text-emerald-800 focus:ring-emerald-800"
                  />
                  <div>
                    <span className={`block text-xs font-black uppercase ${theme === 'light' ? 'text-zinc-900' : 'text-white'}`}>
                      Pay on arrival
                    </span>
                    <span className="block text-[10.5px] text-zinc-400 mt-1 leading-relaxed">
                      Place a reservation request and settle your account in cash or terminal upon physical arrival.
                    </span>
                  </div>
                </label>
              </div>
            </div>

            <div className={`pt-6 border-t flex flex-col md:flex-row justify-between items-center gap-4 ${
              theme === 'light' ? 'border-zinc-100' : 'border-zinc-850'
            }`}>
              <div className="text-left w-full md:w-auto flex flex-col">
                <span className={`text-[10px] font-mono block ${theme === 'light' ? 'text-zinc-400' : 'text-[#A8A29E]'}`}>{t('lblEstimatedTotal')}</span>
                <div className="flex items-baseline gap-2 mt-0.5">
                  {getNightsCount() >= 3 && getActiveRoom() && (
                    <span className="text-zinc-400 dark:text-zinc-500 line-through text-xs font-mono font-semibold">
                      {getNightsCount() * getActiveRoom().pricePerNight} ₴
                    </span>
                  )}
                  <span className={`text-xl font-extrabold font-mono ${theme === 'light' ? 'text-zinc-950' : 'text-[#FAFAFA]'}`}>
                    {calculateTotalPrice() ? `${calculateTotalPrice()} ₴` : '—'}
                  </span>
                </div>
                {getNightsCount() >= 3 && (
                  <span className="text-[10px] font-mono font-black text-blue-600 dark:text-blue-400 mt-1 flex items-center gap-1 animate-pulse">
                    🏷️✨ {getNightsCount() >= 7 ? '15%' : '10%'} {t('longStayDiscount') || 'Long-Stay Discount Applied!'}
                  </span>
                )}
              </div>

              <button
                type="submit"
                disabled={!!pendingBooking || !!bookingError}
                className={`w-full md:w-auto px-8 py-4 cursor-pointer text-white font-mono text-xs font-black uppercase tracking-wider rounded-xl transition-all shadow-md flex items-center justify-center gap-2 ${
                  pendingBooking || bookingError
                    ? (theme === 'light' ? 'bg-zinc-200 text-zinc-400 cursor-not-allowed border border-zinc-350 shadow-none' : 'bg-zinc-800 text-zinc-600 border border-zinc-750 shadow-none cursor-not-allowed')
                    : 'bg-gradient-to-r from-emerald-800 to-emerald-950 hover:from-emerald-700 hover:to-emerald-900 border border-emerald-900/10 shadow-md'
                }`}
              >
                <CreditCard className="w-4 h-4 text-amber-400" />
                <span>{pendingBooking ? t('lblCheckoutReady') : (paymentMethod === 'reception' ? t('btnConfirmReservation') : t('btnProceedToCheckout'))}</span>
              </button>
            </div>
          </form>
        </div>

        {pendingBooking && (
          <LiqPayInlineTerminal
            totalPrice={pendingBooking.totalPrice}
            guestName={pendingBooking.guestName}
            guestPhone={pendingBooking.guestPhone}
            onPaymentSuccess={handleInlinePaymentSuccess}
            onPaymentCancel={() => setPendingBooking(null)}
            onPaymentInitiated={() => {
              if (pendingBooking) {
                return { bookingId: pendingBooking.id, price: pendingBooking.totalPrice };
              }
              return { bookingId: '', price: 0 };
            }}
            currentLanguage={currentLanguage}
            theme={theme}
          />
        )}

        {confirmedBooking && (
          confirmedBooking.paymentId === 'PAY-AT-RECEPTION' ? (
            <div className="booking-modal-reset bg-zinc-950 text-white rounded-[2rem] p-5 sm:p-10 shadow-xl border-2 border-amber-500/80 space-y-4 animate-fade-in font-sans text-left overflow-hidden">
              <div className="flex gap-2 items-center text-amber-350">
                <Clock className="w-5 h-5 text-amber-300 animate-pulse shrink-0" />
                <span className="text-[10px] sm:text-xs font-mono font-black uppercase tracking-widest block font-bold">
                  {PENDING_TITLES[currentLanguage] || PENDING_TITLES['en']}
                </span>
              </div>

              <div className="space-y-2 border-b border-white/10 pb-3">
                <p className="text-xs sm:text-sm text-zinc-150 leading-normal font-sans">
                  {PENDING_DESCS[currentLanguage] || PENDING_DESCS['en']}
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-6 pt-2 font-mono">
                <div className="space-y-2 text-xs sm:text-sm text-zinc-300">
                  <div className="flex flex-wrap justify-between items-center gap-1 border-b border-white/10 pb-1.5">
                    <span className="text-zinc-400">{t('lblConfirmationId') || 'Booking ID:'}</span>
                    <span className="text-amber-300 font-extrabold animate-pulse">{confirmedBooking.id}</span>
                  </div>
                  <div className="flex flex-wrap justify-between items-center gap-1 border-b border-white/10 pb-1.5">
                    <span className="text-zinc-400">{t('lblCheckInDate') || 'Check-In date:'}</span>
                    <span className="text-white font-extrabold">{confirmedBooking.checkIn}</span>
                  </div>
                  <div className="flex flex-wrap justify-between items-center gap-1 border-b border-white/10 pb-1.5">
                    <span className="text-zinc-400">{t('lblCheckOutDate') || 'Check-Out date:'}</span>
                    <span className="text-white font-extrabold">{confirmedBooking.checkOut}</span>
                  </div>
                  {confirmedBooking.guestPhone && (
                    <div className="flex flex-wrap justify-between items-center gap-1 border-b border-white/10 pb-1.5">
                      <span className="text-zinc-400">{t('lblContactPhone') || 'Contact Phone:'}</span>
                      <span className="text-white font-bold break-all">{confirmedBooking.guestPhone}</span>
                    </div>
                  )}
                </div>

                <div className="space-y-2 text-xs sm:text-sm text-zinc-300">
                  <div className="flex flex-wrap justify-between items-center gap-1 border-b border-white/10 pb-1.5">
                    <span className="text-zinc-400">Payment Method:</span>
                    <span className="text-amber-300 font-bold">Pay on arrival</span>
                  </div>
                  <div className="flex flex-wrap justify-between items-center gap-1 font-bold pt-1">
                    <span className="text-zinc-200">Total to Pay on Arrival:</span>
                    <span className="text-amber-300 font-black text-sm sm:text-base">{confirmedBooking.totalPrice} ₴</span>
                  </div>
                </div>
              </div>

              <div className="p-3.5 sm:p-5 bg-amber-50/5 rounded-2xl border border-amber-500/30 space-y-3 mt-4">
                <div className="flex gap-2.5 items-start">
                  <AlertTriangle className="w-5 h-5 text-amber-300 shrink-0 mt-0.5 animate-pulse" />
                  <div className="min-w-0">
                    <h5 className="font-bold text-xs sm:text-sm text-amber-200 uppercase font-mono">
                      {currentLanguage === 'uk' ? 'Необхідно підтвердити заїзд' : 'Action Required for Final Confirmation'}
                    </h5>
                    <p className="text-[11px] sm:text-xs text-zinc-300 mt-1 leading-relaxed font-sans">
                      {currentLanguage === 'uk' 
                        ? 'Щоб гарантувати заїзд, будь ласка, негайно зв’яжіться з адміністрацією Cozzydine одним із способів нижче та вкажіть ваш ID бронювання:'
                        : 'To secure and finalize your reservation, please immediately contact Cozzydine Administration with your Booking ID:'}
                    </p>
                  </div>
                </div>

                <div className="flex flex-col sm:grid sm:grid-cols-3 gap-2.5 pt-2 font-mono">
                  <button
                    type="button"
                    onClick={() => setIsDirectMessagingOpen(true)}
                    className="flex items-center gap-2.5 p-3 bg-amber-500 hover:bg-amber-600 text-zinc-950 rounded-xl transition-all cursor-pointer text-xs uppercase font-black justify-center shadow-md w-full min-w-0"
                  >
                    <MessageSquare className="w-4 h-4 text-zinc-950 shrink-0" />
                    <span className="truncate">{currentLanguage === 'uk' ? 'Напишіть нам напряму' : 'Message Us Directly'}</span>
                  </button>

                  <a
                    href="mailto:cozzydinekyiv@gmail.com?subject=Booking Confirmation"
                    className="flex items-center gap-2.5 p-3 bg-zinc-900 border border-zinc-700 hover:border-zinc-600 text-white rounded-xl transition-all text-xs text-zinc-200 font-bold justify-center w-full min-w-0"
                  >
                    <Mail className="w-4 h-4 text-zinc-400 shrink-0" />
                    <span className="truncate">Email: cozzydinekyiv@gmail.com</span>
                  </a>

                  <a
                    href="tel:+380662070475"
                    className="flex items-center gap-2.5 p-3 bg-zinc-900 border border-zinc-700 hover:border-zinc-600 text-white rounded-xl transition-all text-xs text-zinc-200 font-bold justify-center w-full min-w-0"
                  >
                    <Phone className="w-4 h-4 text-emerald-400 shrink-0 animate-bounce" />
                    <span className="truncate">Phone: +380 66 207 0475</span>
                  </a>
                </div>
              </div>
            </div>
          ) : (
            <div className="bg-emerald-900 text-white rounded-[2rem] p-6 sm:p-10 shadow-xl border border-emerald-950 space-y-4 animate-fade-in font-sans text-left">
              <div className="flex gap-2 items-center text-amber-350">
                <CheckCircle className="w-5 h-5 text-amber-350 animate-bounce" />
                <span className="text-[10px] font-mono font-black uppercase tracking-widest block font-bold">{t('lblReservationConfirmedHeader') || 'RESERVATION CONFIRMED'}</span>
              </div>

              <div className="space-y-2 border-b border-white/10 pb-3">
                <p className="text-sm text-zinc-150 leading-normal">
                  {t('lblCongratulations') || 'Congratulations'}, <strong className="text-white">{confirmedBooking.guestName}</strong>! {t('lblReservedSafely') || 'Your space is reserved safely.'} <strong className="text-amber-300">{confirmedBooking.roomName}</strong>.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-2">
                <div className="space-y-2 font-mono text-xs text-zinc-300">
                  <div className="flex justify-between">
                    <span>{t('lblConfirmationId') || 'Confirmation ID:'}</span>
                    <span className="text-white font-extrabold">{confirmedBooking.id}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>{t('lblCheckInDate') || 'Check-In date:'}</span>
                    <span className="text-white font-extrabold">{confirmedBooking.checkIn}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>{t('lblCheckOutDate') || 'Check-Out date:'}</span>
                    <span className="text-white font-extrabold">{confirmedBooking.checkOut}</span>
                  </div>
                  {confirmedBooking.guestPhone && (
                    <div className="flex justify-between">
                      <span>{t('lblContactPhone') || 'Contact Phone:'}</span>
                      <span className="text-white font-bold">{confirmedBooking.guestPhone}</span>
                    </div>
                  )}
                </div>

                <div className="space-y-2 font-mono text-xs text-zinc-300">
                  <div className="flex justify-between">
                    <span>{t('lblPaidTransaction') || 'Paid Transaction:'}</span>
                    <span className="text-[#a4efb3] font-bold">{confirmedBooking.paymentId.substring(0, 16)}</span>
                  </div>
                  <div className="flex justify-between font-bold">
                    <span>{t('lblTotalSettled') || 'Total Settled:'}</span>
                    <span className="text-amber-300 font-black">{confirmedBooking.totalPrice} ₴</span>
                  </div>
                </div>
              </div>

              <div className="p-4 bg-zinc-950/40 rounded-xl flex gap-2.5 text-xs text-emerald-250 leading-relaxed mt-4">
                <Shield className="w-5 h-5 shrink-0 mt-0.5 text-emerald-400" />
                <span>{t('lblReceptionTip') || 'Present either this Confirmation ID or paid transaction clip at reception desks upon arrival! Lobby is 24/7.'}</span>
              </div>

              <div className="p-4 bg-emerald-950/40 border border-emerald-900/30 rounded-xl flex gap-2.5 text-xs text-emerald-200 leading-relaxed mt-2">
                <Shield className="w-5 h-5 shrink-0 mt-0.5 text-emerald-400" />
                <span>
                  <strong>🔒 Secured:</strong> Your payment has been processed successfully. A confirmation email with details has been sent to your inbox.
                </span>
              </div>
            </div>
          )
        )}

      </div>

      <DirectMessagingModal
        isOpen={isDirectMessagingOpen}
        onClose={() => setIsDirectMessagingOpen(false)}
        bookingId={confirmedBooking?.id || ''}
        currentLanguage={currentLanguage}
      />
    </div>
  );
}
                 
